import { BADGE_SUBMISSION_URL } from 'config/data-sources';
import { talosBadgesFaviconURL } from 'config/constants';
import { pathForNav } from 'shared/lib/nav-paths';

export interface AssociatedLink {
  label: string;
  href: string;
  /** Site or brand logo; HiBob uses official Simple Icons mark */
  iconUrl?: string;
}

const TALOS_BADGES_STATS = pathForNav('stats', null);
const TALOS_BADGES_BATTLE = pathForNav('badges_battle', null);

const SRT_BALANCES_SHEET =
  'https://docs.google.com/spreadsheets/d/12BjF8dGt08OaOtyGiGHeU6QSvFfzpv6rwa2GFSjQEQ0/edit?gid=0';
const SRT_TRANSACTIONS_SHEET =
  'https://docs.google.com/spreadsheets/d/12BjF8dGt08OaOtyGiGHeU6QSvFfzpv6rwa2GFSjQEQ0/edit?gid=1937580390';
const HACKATHON_SHEET =
  'https://docs.google.com/spreadsheets/d/1oAhkMPrakGsVGBKg0QAJXL2VWJMImSvOgPQE1y0ddtQ/edit?usp=sharing';
const CHALLENGE_1_GR86_OSCHERS_SHEET =
  'https://docs.google.com/spreadsheets/d/1IQZ2t4Bej01bm6jpv06plXIQNHIMVYYaIWuM_-UPV70/edit?usp=sharing';
const HIBOB = 'https://app.hibob.com/';
const JIRA_MY_CREATED = 'https://talostrading.atlassian.net/issues/?filter=-2';
const JIRA_SUPPORT_RESOLVED =
  'https://talostrading.atlassian.net/issues/?jql=project%20in%20%28CLIENT%2C%20CSL%29%20AND%20status%20in%20%28Canceled%2C%20Closed%2C%20Completed%2C%20Declined%2C%20Done%2C%20Failed%2C%20Published%2C%20%22Ready%20To%20Release%22%2C%20Rejected%2C%20Resolved%29%20AND%20assignee%20in%20%28currentUser%28%29%29%20order%20by%20created%20DESC';
const CIPHER = 'https://taloschallenge.netlify.app/';
const ASHBY_INTERVIEW_HISTORY = 'https://app.ashbyhq.com/interview-history';
const ASHBY_UPCOMING = 'https://app.ashbyhq.com/home/upcoming';
const JIRA_IT_TICKETS = 'https://talostrading.atlassian.net/servicedesk/customer/user/requests?statuses=closed';

export const LINKS_JIRA_IT_TICKETS: AssociatedLink[] = [
  {
    label: 'IT Tickets',
    href: JIRA_IT_TICKETS,
    iconUrl: favicon('atlassian.com'),
  },
];
/**
 * Site icon via icon.horse (https://icon.horse) — returns a direct favicon/apple-touch image.
 * Google's s2/favicons often 302s to gstatic with weak fallbacks and mismatched marks for image tags.
 */
function favicon(host: string): string {
  const h = host.toLowerCase().replace(/^www\./, '');
  return `https://icon.horse/icon/${encodeURIComponent(h)}?size=64`;
}

/** Map URL hostnames to the brand domain icon.horse resolves best. */
function normalizeHostForIcon(hostname: string): string {
  const h = hostname.toLowerCase().replace(/^www\./, '');
  if (h.endsWith('.atlassian.net')) return 'atlassian.com';
  if (h.endsWith('.netlify.app')) return 'netlify.com';
  if (h.startsWith('app.') && h.split('.').length >= 3) return h.slice(4);
  return h;
}

/** Resolve icon for a link (explicit icon, else hostname favicon). */
export function iconForAssociatedLink(link: AssociatedLink): string {
  if (link.iconUrl) return link.iconUrl;
  try {
    return favicon(normalizeHostForIcon(new URL(link.href).hostname));
  } catch {
    return favicon('example.com');
  }
}

/** Talos Badges stats only (no SRT spreadsheet). */
export const LINKS_TALOS_STATS: AssociatedLink[] = [
  {
    label: 'Talos Stats',
    href: TALOS_BADGES_STATS,
    iconUrl: favicon('talosbadges.com'),
  },
];

/** Battle Badges at talosbadges.com/battle/badges. */
export const LINKS_BATTLE: AssociatedLink[] = [
  {
    label: 'Battle Badges',
    href: TALOS_BADGES_BATTLE,
    iconUrl: favicon('talosbadges.com'),
  },
];

/** Same Google Form as the sidebar “Submit a Badge Idea” action (opens in a new tab). */
export const LINKS_SUBMIT_BADGE_IDEA: AssociatedLink[] = [
  {
    label: 'Submit a Badge Idea',
    href: BADGE_SUBMISSION_URL,
    iconUrl: talosBadgesFaviconURL,
  },
];

/** Most SRT badges: one useful link. Some use balances + transactions (see `TALOS_BADGES_SRT_BALANCES_AND_TRANSACTIONS_LINKS`). */
const SRT_SHEET_ICON = favicon('docs.google.com');

export const TALOS_BADGES_SRT_TRANSACTIONS_LINK: AssociatedLink[] = [
  {
    label: 'SRT Transactions',
    href: SRT_TRANSACTIONS_SHEET,
    iconUrl: SRT_SHEET_ICON,
  },
];

export const TALOS_BADGES_SRT_BALANCES_LINK: AssociatedLink[] = [
  { label: 'SRT Balances', href: SRT_BALANCES_SHEET, iconUrl: SRT_SHEET_ICON },
];

export const TALOS_BADGES_SRT_REDEMPTIONS_LINK: AssociatedLink[] = [
  {
    label: 'SRT Redemptions',
    href: SRT_TRANSACTIONS_SHEET,
    iconUrl: SRT_SHEET_ICON,
  },
];

/** SRT Balances + SRT Transactions (e.g. Distributooor, In Debt!). */
export const TALOS_BADGES_SRT_BALANCES_AND_TRANSACTIONS_LINKS: AssociatedLink[] = [
  ...TALOS_BADGES_SRT_BALANCES_LINK,
  ...TALOS_BADGES_SRT_TRANSACTIONS_LINK,
];

export const LINKS_TENURE: AssociatedLink[] = [{ label: 'HiBob', href: HIBOB, iconUrl: favicon('app.hibob.com') }];

export const LINKS_HACKATHON: AssociatedLink[] = [
  {
    label: 'AI: Unleashed',
    href: HACKATHON_SHEET,
    iconUrl: favicon('docs.google.com'),
  },
];

/** Hot Lap Challenge + Talos Racing badges */
export const LINKS_CHALLENGE_1_GR86_OSCHERS: AssociatedLink[] = [
  {
    label: '1 - Toyota GR86 @ Oschers',
    href: CHALLENGE_1_GR86_OSCHERS_SHEET,
    iconUrl: favicon('docs.google.com'),
  },
];

export const LINKS_NYT_CONNECTIONS: AssociatedLink[] = [
  {
    label: 'NYT Connections',
    href: 'https://www.nytimes.com/games/connections',
    iconUrl: favicon('nytimes.com'),
  },
];

export const LINKS_COMMITS: AssociatedLink[] = [
  {
    label: 'Ava - contributors',
    href: 'https://github.com/talostrading/Ava/graphs/contributors',
    iconUrl: favicon('github.com'),
  },
  {
    label: 'Ava-UI - contributors',
    href: 'https://github.com/talostrading/Ava-UI/graphs/contributors',
    iconUrl: favicon('github.com'),
  },
  {
    label: 'Conrad - contributors',
    href: 'https://github.com/talostrading/Conrad/graphs/contributors',
    iconUrl: favicon('github.com'),
  },
  {
    label: 'Secmaster - contributors',
    href: 'https://github.com/talostrading/secmaster/graphs/contributors',
    iconUrl: favicon('github.com'),
  },
];

export const LINKS_TELETOOLS: AssociatedLink[] = [
  {
    label: 'GitHub - Teletools',
    href: 'https://github.com/talostrading/teletools',
    iconUrl: favicon('github.com'),
  },
];

/** Your merged PRs per repo (GitHub treats @me as the signed-in user). */
const MERGED_PRS_Q = 'sort%3Aupdated-desc+is%3Amerged+author%3A%40me';

/** Merge Machine — Ava / Ava-UI only */
export const LINKS_MERGED_PRS: AssociatedLink[] = [
  {
    label: 'Ava - merged PRs',
    href: `https://github.com/talostrading/Ava/pulls?q=${MERGED_PRS_Q}`,
    iconUrl: favicon('github.com'),
  },
  {
    label: 'Ava-UI - merged PRs',
    href: `https://github.com/talostrading/Ava-UI/pulls?q=${MERGED_PRS_Q}`,
    iconUrl: favicon('github.com'),
  },
];

export const LINKS_JIRA_CREATED: AssociatedLink[] = [
  {
    label: '# Jira Created',
    href: JIRA_MY_CREATED,
    iconUrl: favicon('atlassian.com'),
  },
];

export const LINKS_JIRA_SUPPORT_RESOLVED: AssociatedLink[] = [
  {
    label: '# Support Resolved',
    href: JIRA_SUPPORT_RESOLVED,
    iconUrl: favicon('atlassian.com'),
  },
];

export const LINKS_CIPHER_CHALLENGE: AssociatedLink[] = [
  { label: 'Cipher Challenge', href: CIPHER, iconUrl: favicon('netlify.com') },
];

/** Interview conduct badges (conducted interviews) — Interview History */
export const LINKS_ASHBY_INTERVIEW_HISTORY: AssociatedLink[] = [
  {
    label: 'Ashby Interview History',
    href: ASHBY_INTERVIEW_HISTORY,
    iconUrl: favicon('ashbyhq.com'),
  },
];

/** Referrals / recruiting milestones — Ashby pipeline home */
export const LINKS_INTERVIEW_RECRUITING: AssociatedLink[] = [
  {
    label: 'Ashby Home',
    href: ASHBY_UPCOMING,
    iconUrl: favicon('ashbyhq.com'),
  },
];

export const LINKS_LOCAL_COFFEE: AssociatedLink[] = [
  {
    label: 'Local Coffee',
    href: 'https://talostrading.atlassian.net/wiki/spaces/TALOS/pages/3717464182/Local+Coffee',
    iconUrl: favicon('atlassian.com'),
  },
];

export const LINKS_WORLD_CUP_SWEEPSTAKE: AssociatedLink[] = [
  {
    label: 'World Cup Sweepstake',
    href: 'https://world-cup-2026-sweepstake.talostrading.chatgpt-team.site/',
    // Soccer ball (U+26BD) via Twemoji — clearer than the site favicon.
    iconUrl: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/26bd.png',
  },
];

export const LINKS_TYPING_RECORDS: AssociatedLink[] = [
  {
    label: 'MonkeyType Typing Test',
    href: 'https://monkeytype.com/',
    iconUrl: favicon('monkeytype.com'),
  },
];
